import React from "react";
import entete from "../../../../assets/img/entete_pool.png";
import { chunkArray } from "../../../../variables/functions";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import { DocumentFooter } from "../../../../components/docs/DocumentFooter";
import BarreTop from "../../../../components/docs/BarreTop";
// Règles @page portrait + marges d'impression.
import "./styles/flotteCPStyle.css";

// Capacité d'une page A4 portrait pour un nom Assuré tenant sur UNE ligne.
// ⚠️ La page a une hauteur FIXE (maxHeight 297mm) : au-delà de la capacité, les lignes en trop sont
// COUPÉES (perdues). Quand le nom Assuré est long et passe sur 2+ lignes, chaque ligne est plus haute
// → on réduit le nombre d'engins par page (rowsPerPage calculé dans le composant) pour éviter la coupe.
const BASE_ROWS_PER_PAGE = 27;
// Nombre approximatif de caractères tenant sur une ligne de la colonne « Assuré » ÉLARGIE (~31%).
const ASSURE_CHARS_PER_LINE = 34;

// Colonnes de garanties prioritaires (toujours en tête, dans cet ordre).
const PRIORITY = ["RC", "IC", "Vol", "Incendie"];

// Normalise une garantie vers un libellé de colonne. RC + surprimes → "RC" ;
// Individuel Conducteur → "IC" ; Vol → "Vol" ; Incendie → "Incendie" ;
// toute autre garantie conserve son nom propre (CEDEAO, supplémentaires...).
const labelGarantie = (nom) => {
  const n = (nom || "").toUpperCase();
  if (n.startsWith("RC") || n.startsWith("SURPRIME")) return "RC";
  if (n.includes("INDIVIDUEL") || n.includes("CONDUCTEUR")) return "IC";
  if (n.startsWith("VOL")) return "Vol";
  if (n.startsWith("INC")) return "Incendie";
  return (nom || "").trim() || "Autres";
};

// Liste ordonnée des colonnes de garanties présentes sur la flotte.
const buildColumns = (engins) => {
  const seen = [];
  engins.forEach((e) => {
    (e?.compulsoryGuaranties || []).forEach((g) => {
      const label = labelGarantie(g?.nom);
      if (label && !seen.includes(label)) seen.push(label);
    });
  });
  const priority = PRIORITY.filter((p) => seen.includes(p));
  const others = seen.filter((s) => !PRIORITY.includes(s));
  return [...priority, ...others];
};

// Primes nettes par colonne pour un engin. `factor` < 1 = prorata (ex. incorporation),
// 1 = prime annuelle pleine.
const montantsParColonne = (engin, factor = 1) => {
  const map = {};
  (engin?.compulsoryGuaranties || []).forEach((g) => {
    const label = labelGarantie(g?.nom);
    const montant = (Number(g?.primeNetteAnnuelle ?? g?.primeNette) || 0) * factor;
    map[label] = (map[label] || 0) + montant;
  });
  return map;
};

const puissanceLabel = (e) => {
  // Motos : le volume (cm³) est l'attribut réel à afficher.
  // Véhicules : afficher la puissance d'ORIGINE — le champ `volume` contient
  // l'équivalent essence CONVERTI pour le DIESEL (utilisé pour la tarification),
  // pas la puissance réelle ; l'afficher induirait l'utilisateur en erreur.
  const isMoto = `${e?.categorie?.codeCategorie ?? ""}`.toUpperCase().startsWith("M");
  if (isMoto) {
    if (e?.volume) return `${e.volume} cm³`;
    if (e?.puissance) return `${e.puissance}`;
    return "—";
  }
  if (e?.puissance) return `${e.puissance} CV`;
  if (e?.volume) return `${e.volume} CV`;
  return "—";
};

const anneeLabel = (e) => {
  const d = e?.dateMiseEnCirculation;
  if (!d) return "—";
  const m = String(d).match(/(\d{4})/);
  return m ? m[1] : String(d);
};

// Assuré du véhicule : la personne portée par l'engin, sinon le conducteur habituel,
// sinon le souscripteur (fallback fourni par le parent via la prop `assure`).
const assureLabel = (engin, souscripteur) => {
  const p = engin?.conducteurHabituel || engin?.personne;
  const nom = `${p?.nom || ""} ${p?.prenom || ""}`.trim();
  return nom || souscripteur || "—";
};

const portraitPage = {
  width: "210mm",
  minHeight: "297mm",
  maxHeight: "297mm",
  padding: "10mm",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

const thBlue = { border: "1px solid #000", padding: "4px 6px", fontSize: "11px", backgroundColor: "#dbe5f1", textAlign: "center" };
const thOrange = { border: "1px solid #000", padding: "4px 6px", fontSize: "11px", backgroundColor: "#fcefd0", textAlign: "center" };
const thBase = { border: "1px solid #000", padding: "4px 6px", fontSize: "11px" };
const tdBase = { border: "1px solid #000", padding: "3px 6px", fontSize: "11px" };
const tdNum = { ...tdBase, textAlign: "right", whiteSpace: "nowrap" };

const AnnexeFlotte = ({
  engins = [],
  numeroPolice,
  assure,
  logoPreviewUrl,
  showLogo = true,
  titreEngins = "ANNEXE 1 FLOTTE : Les engins",
  titreGaranties = "ANNEXE 2 FLOTTE : Les garanties souscrites",
  prorataFactor = 1,
}) => {
  const cols = buildColumns(engins);
  // Nombre d'engins par page ADAPTATIF : si un nom Assuré est long (2+ lignes dans la colonne), chaque
  // ligne est plus haute → on réduit la capacité pour ne rien couper (page à hauteur fixe).
  const maxAssureLen = engins.reduce(
    (m, e) => Math.max(m, (assureLabel(e, assure) || "").length), 0);
  const lignesNom = Math.max(1, Math.ceil(maxAssureLen / ASSURE_CHARS_PER_LINE));
  // Annexe 1 (caractéristiques) : pas de colonne Assuré → lignes sur 1 ligne → capacité pleine.
  const rowsPerPageEngins = BASE_ROWS_PER_PAGE;
  // Annexe 2 (garanties) : la colonne Assuré peut passer sur 2+ lignes → capacité réduite d'autant.
  const rowsPerPageGaranties = Math.max(8, Math.floor(BASE_ROWS_PER_PAGE / lignesNom));
  const pagesEngins = chunkArray(engins, rowsPerPageEngins);
  const pagesGaranties = chunkArray(engins, rowsPerPageGaranties);
  // Prorata appliqué aux primes (ex. incorporation) : < 1 ⇒ prime au prorata, 1 ⇒ annuelle.
  const facteur = (typeof prorataFactor === "number" && prorataFactor > 0) ? prorataFactor : 1;
  const estProrata = facteur !== 1;

  // Totaux par colonne de garantie (Annexe 2).
  const totaux = {};
  cols.forEach((c) => (totaux[c] = 0));
  engins.forEach((e) => {
    const m = montantsParColonne(e, facteur);
    cols.forEach((c) => (totaux[c] += m[c] || 0));
  });
  // Grand total = prime nette totale annuelle de toute la flotte.
  const grandTotal = cols.reduce((s, c) => s + (totaux[c] || 0), 0);

  const EnTete = () => (
    <div style={{ flexShrink: 0, marginBottom: "4mm" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4mm" }}>
        {showLogo && (
          <img
            src={logoPreviewUrl}
            alt=""
            style={{ maxWidth: "42%", maxHeight: "78px", width: "auto", height: "auto", objectFit: "contain" }}
          />
        )}
        <img
          src={entete}
          alt=""
          style={{ maxWidth: showLogo ? "54%" : "100%", maxHeight: "78px", width: "auto", height: "auto", objectFit: "contain" }}
        />
      </div>
      <BarreTop />
    </div>
  );

  const Titre = ({ titre }) => (
    <div style={{ flexShrink: 0, marginBottom: "3mm" }}>
      <div style={{ fontWeight: "bold", fontSize: "13px", color: "#272366" }}>{titre}</div>
      <div style={{ fontSize: "11px" }}>
        N° Police : <strong>{numeroPolice || "—"}</strong>
      </div>
      <div style={{ fontSize: "11px" }}>
        Souscripteur : <strong>{assure || "—"}</strong>
      </div>
    </div>
  );

  return (
    <>
      {/* ── ANNEXE 1 : caractéristiques des engins ─────────────────────────────── */}
      {pagesEngins.map((chunk, pageIndex) => (
        <div
          key={`a1-${pageIndex}`}
          className="annexe-page"
          style={{
            ...portraitPage,
            pageBreakBefore: pageIndex === 0 ? "always" : "auto",
            pageBreakAfter: "always",
          }}
        >
          <EnTete />
          <Titre titre={titreEngins} />
          <div style={{ flex: 1, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th colSpan={9} style={thBlue}>Caractéristiques des engins de la flotte</th>
                </tr>
                <tr>
                  <th style={thBase}>N°</th>
                  <th style={thBase}>N° Imm</th>
                  <th style={thBase}>Chassis</th>
                  <th style={thBase}>Puissance</th>
                  <th style={thBase}>Nbre Place</th>
                  <th style={thBase}>Energie</th>
                  <th style={thBase}>Marque</th>
                  <th style={thBase}>Année</th>
                  <th style={thBase}>Usage</th>
                </tr>
              </thead>
              <tbody>
                {chunk.map((engin, index) => (
                  <tr key={index}>
                    <td style={{ ...tdBase, textAlign: "center" }}>{index + 1 + rowsPerPageEngins * pageIndex}</td>
                    <td style={tdBase}>{engin?.immatriculation || "—"}</td>
                    <td style={tdBase}>{engin?.numeroChassis || "—"}</td>
                    <td style={tdBase}>{puissanceLabel(engin)}</td>
                    <td style={{ ...tdBase, textAlign: "center" }}>{engin?.nombrePlace ?? "—"}</td>
                    <td style={tdBase}>{engin?.energie || "—"}</td>
                    <td style={tdBase}>{engin?.marque || "—"}</td>
                    <td style={{ ...tdBase, textAlign: "center" }}>{anneeLabel(engin)}</td>
                    <td style={tdBase}>{engin?.usage || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ flexShrink: 0, marginTop: "4mm" }}>
            <DocumentFooter />
          </div>
        </div>
      ))}

      {/* ── ANNEXE 2 : garanties souscrites ────────────────────────────────────── */}
      {pagesGaranties.map((chunk, pageIndex) => {
        const isLast = pageIndex === pagesGaranties.length - 1;
        return (
          <div
            key={`a2-${pageIndex}`}
            className="annexe-page"
            style={{
              ...portraitPage,
              pageBreakAfter: isLast ? "auto" : "always",
            }}
          >
            <EnTete />
            <Titre titre={titreGaranties} />
            <div style={{ flex: 1, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ ...thBase, width: "4%" }}>N°</th>
                    <th rowSpan={2} style={{ ...thBase, width: "11%" }}>N° Imm</th>
                    {/* Assuré ÉLARGI (les noms longs tiennent sur moins de lignes). */}
                    <th rowSpan={2} style={{ ...thBase, width: "31%" }}>Assuré</th>
                    <th rowSpan={2} style={{ ...thBase, width: "11%" }}>N° Attestation</th>
                    <th colSpan={cols.length || 1} style={thOrange}>
                      Garanties souscrites ({estProrata ? "prime nette au prorata" : "prime nette annuelle"} — F CFA)
                    </th>
                    {/* Prime nette totale RÉTRÉCIE (les montants sont courts). */}
                    <th rowSpan={2} style={{ ...thBase, textAlign: "right", backgroundColor: "#e2efda", width: "9%" }}>
                      Prime nette totale
                    </th>
                  </tr>
                  <tr>
                    {cols.map((c) => (
                      <th key={c} style={{ ...thBase, textAlign: "right" }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chunk.map((engin, index) => {
                    const m = montantsParColonne(engin, facteur);
                    const totalEngin = cols.reduce((s, c) => s + (m[c] || 0), 0);
                    return (
                      <tr key={index}>
                        <td style={{ ...tdBase, textAlign: "center" }}>{index + 1 + rowsPerPageGaranties * pageIndex}</td>
                        <td style={tdBase}>{engin?.immatriculation || "—"}</td>
                        <td style={tdBase}>{assureLabel(engin, assure)}</td>
                        <td style={tdBase}>{engin?.numeroAttestation || "—"}</td>
                        {cols.map((c) => (
                          <td key={c} style={tdNum}>{m[c] ? displayNumbers(m[c]) : "—"}</td>
                        ))}
                        <td className="force-bold" style={{ ...tdNum, fontWeight: "bold", backgroundColor: "#f3f9f0" }}>
                          {displayNumbers(totalEngin)}
                        </td>
                      </tr>
                    );
                  })}
                  {isLast && (
                    <tr className="force-bold" style={{ fontWeight: "bold", backgroundColor: "#e2efda" }}>
                      <td colSpan={4} style={{ ...tdBase, textAlign: "center" }}>Totaux</td>
                      {cols.map((c) => (
                        <td key={c} style={tdNum}>{displayNumbers(totaux[c])}</td>
                      ))}
                      <td style={tdNum}>{displayNumbers(grandTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ flexShrink: 0, marginTop: "4mm" }}>
              <DocumentFooter />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default AnnexeFlotte;
